import React, { FC, KeyboardEvent, memo, useState } from 'react';
import { Box, Button, ButtonProps, HStack } from '@chakra-ui/react';
import { useCheckoutState, useGlobalUIState } from '@olo-web/client-state';
import { usePlaceOrder } from '../../hooks/usePlaceOrder';
import { useIsOrderButtonHidden } from '../../hooks/useIsOrderButtonHidden';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { useIsDelivery } from '@olo-web/utils/common/hooks';
import { MotionBox } from '@olo-web/components/atoms/motion/MotionBox.ui';
import { useCheckoutOrderTotal } from '@olo-web/domain/orders/hooks/useCheckoutOrderTotal';
import { useIsDineIn } from '@olo-web/utils/common/hooks/useIsDineIn';

type T = ButtonProps & {
  isScanAndPay?: boolean;
  onClick?: () => any;
  onSuccess?: () => any;
  onError?: () => void;
};

const CheckoutOrderButtons: FC<T> = memo(({ isScanAndPay, onClick, ...props }) => {
  const {
    isApplePay,
    isGooglePay,
    isMobilePayment,
    giftCardCoversEntireOrderAmount,
    isZeroChargeOrder,
  } = usePaymentMethodValues();
  const [isOrderPlaced, setPlacedOrder] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const isDelivery = useIsDelivery();
  const { isLoading } = useCheckoutState();
  const { routerLoading } = useGlobalUIState();
  const orderTotal = useCheckoutOrderTotal();
  const isBalanceZero = giftCardCoversEntireOrderAmount || isZeroChargeOrder;
  const orderText = isDelivery ? 'delivery' : 'pickup';
  const isDineIn = useIsDineIn();
  const focusRingStyle = {
    outline: 'none',
    boxShadow: '0 0 0 2px #fff, 0 0 0 5px #000',
  };

  const onError = () => {
    setPlacedOrder(false);
    props.onError?.();
  };
  const onSuccess = () => {
    setPlacedOrder(false);
    props.onSuccess?.();
  };

  const placeOrder = usePlaceOrder({ onSuccess: onSuccess, onError: onError });

  const handlePlaceOrder = async () => {
    setIsVerifying(true);
    setPlacedOrder(true);

    try {
      // Always proceed with order placement
      await placeOrder();
      onClick?.();
    } catch (error) {
      console.error('Error during order placement:', error);
      onError();
    } finally {
      setIsVerifying(false);
      setPlacedOrder(false);
    }
  };

  const handleOrderButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handlePlaceOrder();
  };

  if (isOrderPlaced || isLoading || isVerifying) {
    return (
      <Button
        w="100%"
        colorScheme={isMobilePayment ? 'blackAlpha' : 'primary'}
        size="md"
        isLoading={true}
        fontSize="lg"
        loadingText="Placing your order..."
        _focus={{ boxShadow: 'none' }}
        _focusVisible={focusRingStyle}
        {...props}
      />
    );
  }

  if (isApplePay && !isBalanceZero) {
    return <div id="cjs-applepay" style={{ width: '100%' }} />;
  }

  if (isGooglePay && !isBalanceZero) {
    return <Box as="div" id="cjs-googlepay" />;
  }

  return (
    <Button
      onClick={handlePlaceOrder}
      onKeyDown={handleOrderButtonKeyDown}
      w="100%"
      colorScheme="primary"
      size="md"
      type="submit"
      fontSize="lg"
      disabled={routerLoading || isOrderPlaced || isLoading || isVerifying}
      {...props}
      data-testid="place-order-button"
      _focus={{ boxShadow: 'none' }}
      _focusVisible={focusRingStyle}
    >
      {isScanAndPay || isDineIn ? (
        <HStack justify="space-between" w="100%" px={2}>
          <Box as="span">Pay now</Box>
          <Box as="span">{orderTotal}</Box>
        </HStack>
      ) : (
        `Place order for ${orderText}`
      )}
    </Button>
  );
});

const animations = {
  visible: {
    height: 'auto',
    opacity: 1,
  },
  hidden: {
    opacity: 0,
    height: '0px',
  },
};

export const CheckoutOrderButton: FC<T> = (props) => {
  const { isZeroChargeOrder } = usePaymentMethodValues();
  const isOrderButtonHidden = useIsOrderButtonHidden();

  return (
    <>
      <MotionBox
        variants={animations}
        animate={isOrderButtonHidden && !isZeroChargeOrder ? 'hidden' : 'visible'}
        width="100%"
        overflow="visible"
      >
        <CheckoutOrderButtons {...props} />
      </MotionBox>
    </>
  );
};

export default CheckoutOrderButton;
